/**
 * אנשי חוץ — the unit's directory of people who are not on its מצבה.
 *
 * A {@link ExternalPerson} typed into a team form belongs to that mission and to
 * nothing else. Some of them come back, though — the same contractor every
 * fortnight, the same driver from the neighbouring company — and re-typing a
 * name, a phone and a plate every time is how a number ends up wrong on a
 * dispatch order.
 *
 * So the team form offers to keep one: "שמור כאיש חוץ לפעמים הבאות". A kept
 * person is filed *here*, in a category of its own, and deliberately nowhere
 * near the two rosters the unit's readiness is counted off:
 *
 *  - not in `soldiers`, so the מצבה, the availability counters, the workload
 *    graph and the licence engine keep counting only the unit's own people;
 *  - not in `vehicles`, so a contractor's van never appears in the fleet, never
 *    takes a מספר פנימי and never turns up in a vehicle-availability light.
 *
 * The record is a *contact card*, not a soldier: it holds what has to be typed
 * again next time and nothing else. The rules that clean one up live in
 * `src/core/external-people.ts`, and the store in
 * `src/db/external-people-store.ts`.
 */
import type { BaseEntity, ISODateString } from './index';
import type { CrewRole } from './assignment';

export { EXTERNAL_PEOPLE_CATEGORY, EXTERNAL_PERSON_TAG, TEMPORARY_VEHICLE_LABEL } from './assignment';

/** The wording the "keep this one" checkbox is offered with. */
export const SAVE_EXTERNAL_PERSON_LABEL = 'שמור כאיש חוץ לפעמים הבאות';

/**
 * One kept external person. Everything but the name is optional, because the
 * form itself only demands a name — a card holding a name and a phone is still
 * worth more than re-typing both.
 */
export interface ExternalPersonRecord extends BaseEntity {
  fullName: string;
  /** The seat they usually take — what the form pre-selects next time. */
  defaultCrewRole?: CrewRole;
  /** מספר אישי, for somebody who has one. */
  personalNumber?: string;
  phone?: string;
  /** Free text — the job or the body they come from. */
  role?: string;
  /** The vehicle they usually arrive in — never a fleet record. */
  vehicleModel?: string;
  plateNumber?: string;
  notes?: string;
  /** How many times they have been placed on a task from this directory. */
  useCount: number;
  /** When they were last placed — what the picker sorts the list by. */
  lastUsedAt?: ISODateString;
}

/** A kept person as the form hands them over — the id and stamps are ours. */
export type ExternalPersonRecordDraft = Partial<Omit<ExternalPersonRecord, 'fullName'>> & {
  fullName: string;
};
